import { defineStore } from 'pinia'

const NOTIFICATION_TTL_MS = 30_000
const MAX_VISIBLE_NOTIFICATIONS = 3

export interface ChatPopupNotificationInput {
    projectId: string
    chatId: string
    messageId: string
    title: string
    text: string
    provider?: string
    timestamp?: string
}

export interface ChatPopupNotification extends ChatPopupNotificationInput {
    id: string
    remainingMs: number
    startedAt: number | null
    timerId: number | null
}

interface ChatNotificationsState {
    notifications: ChatPopupNotification[]
}

const truncate = (value: string, maxLength: number) => {
    if (value.length <= maxLength) return value
    return `${value.slice(0, maxLength).trim()}...`
}

export const useChatNotificationsStore = defineStore('chatNotifications', {
    state: (): ChatNotificationsState => ({
        notifications: [],
    }),

    actions: {
        push(input: ChatPopupNotificationInput) {
            if (this.notifications.some((item) => item.messageId === input.messageId)) {
                return
            }

            while (this.notifications.length >= MAX_VISIBLE_NOTIFICATIONS) {
                this.remove(this.notifications[0].id)
            }

            const notification: ChatPopupNotification = {
                ...input,
                id: `${input.messageId}-${Date.now()}`,
                title: truncate(input.title || 'Новое сообщение', 80),
                text: truncate(input.text || '', 100),
                remainingMs: NOTIFICATION_TTL_MS,
                startedAt: null,
                timerId: null,
            }

            this.notifications.push(notification)
            this.startTimer(notification.id)
        },

        remove(id: string) {
            const notification = this.notifications.find((item) => item.id === id)
            if (notification?.timerId) {
                window.clearTimeout(notification.timerId)
            }
            this.notifications = this.notifications.filter((item) => item.id !== id)
        },

        pauseAll() {
            const now = Date.now()
            this.notifications.forEach((notification) => {
                if (notification.timerId) {
                    window.clearTimeout(notification.timerId)
                    notification.timerId = null
                }
                if (notification.startedAt) {
                    notification.remainingMs = Math.max(
                        0,
                        notification.remainingMs - (now - notification.startedAt)
                    )
                    notification.startedAt = null
                }
            })
        },

        resumeAll() {
            this.notifications.forEach((notification) => {
                this.startTimer(notification.id)
            })
        },

        startTimer(id: string) {
            const notification = this.notifications.find((item) => item.id === id)
            if (!notification || notification.timerId || document.hidden) {
                return
            }

            notification.startedAt = Date.now()
            notification.timerId = window.setTimeout(() => {
                this.remove(id)
            }, notification.remainingMs)
        },
    },
})